import React, { useEffect, useMemo, useState } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  Button,
  TextField,
  Stack,
  Box,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Drawer,
  Tabs,
  Tab,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import PersonIcon from '@mui/icons-material/Person';
import { listInactiveEmployees, unarchiveEmployee } from '../services/employee';
import { fetchCurrentUser } from '../services/auth';
import payslipsService from '../services/payslips';
import leavesService from '../services/leaves';
import customFieldsService from '../services/customFields';
import exitAuthService from '../services/exitAuthorizations';
import dechargeService from '../services/dechargeArgents';
import api from '../services/api';

const InactifsPage = () => {
  const [rows, setRows] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState('custom');
  const [loadingTab, setLoadingTab] = useState(false);
  const [customFields, setCustomFields] = useState(null);
  const [payslips, setPayslips] = useState(null);
  const [leaves, setLeaves] = useState(null);
  const [missions, setMissions] = useState(null);
  const [exitAuths, setExitAuths] = useState(null);
  const [decharges, setDecharges] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await listInactiveEmployees();
      const data = Array.isArray(res.data) ? res.data : [];
      // Flatten nested associations to explicit fields consumed by the grid
      const mapped = data.map((r) => ({
        ...r,
        profession_name: r?.profession?.name || '',
        site_name: r?.site?.name || '',
      }));
      // Debug: inspect one sample row in devtools
      if (mapped.length > 0) {
        // eslint-disable-next-line no-console
        console.debug('Inactifs sample row:', mapped[0]);
      }
      setRows(mapped);
    } catch (e) {
      console.error('Erreur chargement inactifs:', e);
      setSnack({ open: true, message: "Erreur de chargement", severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const openDetails = (employee) => {
    setSelectedEmployee(employee);
    setDrawerOpen(true);
    setActiveTab('custom');
    setCustomFields(null);
    setPayslips(null);
    setLeaves(null);
    setMissions(null);
    setExitAuths(null);
    setDecharges(null);
  };

  const closeDetails = () => {
    setDrawerOpen(false);
    setSelectedEmployee(null);
  };

  const loadTabData = async (tabKey, empId) => {
    setLoadingTab(true);
    try {
      if (tabKey === 'custom') {
        const [metaRes, fieldsRes] = await Promise.all([
          customFieldsService.getEmployeeCustomFieldsMetadata(empId),
          customFieldsService.getEmployeeCustomFields(empId),
        ]);
        const meta = metaRes.data || {};
        // Normalize metadata: accept either { order, fields } or flat { key: meta }
        const hasStructured = meta && (Array.isArray(meta.order) || meta.fields);
        const normalized = hasStructured
          ? { order: meta.order || Object.keys(meta.fields || {}), fields: meta.fields || {} }
          : { order: Object.keys(meta), fields: meta };
        setCustomFields({ metadata: normalized, values: fieldsRes.data });
      } else if (tabKey === 'payslips') {
        const res = await payslipsService.listPayslips(empId);
        setPayslips(res.data || []);
      } else if (tabKey === 'leaves') {
        const res = await leavesService.listByEmployee(empId);
        const onlyApproved = (res.data || []).filter((l) => l?.status === 'approved');
        setLeaves(onlyApproved);
      } else if (tabKey === 'missions') {
        const res = await api.get('/mission-orders', { params: { employee_id: empId } });
        const onlyValidated = (res.data || []).filter((m) => m?.validated === true && m?.refused !== true);
        setMissions(onlyValidated);
      } else if (tabKey === 'exit') {
        const res = await exitAuthService.listExitAuthorizations({ employee_id: empId });
        const onlyValidated = (res.data || []).filter((ea) => ea?.validated === true && ea?.refused !== true);
        setExitAuths(onlyValidated);
      } else if (tabKey === 'decharge') {
        const res = await dechargeService.listDechargeArgents({ employee_id: empId });
        const onlyValidated = (res.data || []).filter((d) => d?.validated === true && d?.refused !== true);
        setDecharges(onlyValidated);
      }
    } catch (e) {
      console.error('Erreur chargement onglet:', tabKey, e);
      setSnack({ open: true, message: 'Erreur de chargement', severity: 'error' });
    } finally {
      setLoadingTab(false);
    }
  };

  useEffect(() => {
    if (drawerOpen && selectedEmployee) {
      // Load default tab on open
      loadTabData('custom', selectedEmployee.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, selectedEmployee?.id]);

  useEffect(() => {
    (async () => {
      const me = await fetchCurrentUser();
      setIsAdmin(me?.role === 1 || me?.role_id === 1);
      await load();
    })();
  }, []);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  const handleAskUnarchive = (id) => {
    setConfirmId(id);
    setConfirmOpen(true);
  };

  const handleConfirmUnarchive = async () => {
    if (!confirmId) return;
    try {
      await unarchiveEmployee(confirmId);
      setSnack({ open: true, message: 'Employé désarchivé', severity: 'success' });
      await load();
    } catch (e) {
      console.error('Unarchive error:', e);
      setSnack({ open: true, message: "Échec du désarchivage", severity: 'error' });
    } finally {
      setConfirmOpen(false);
      setConfirmId(null);
    }
  };

  const columns = [
    {
      field: 'profile_picture',
      headerName: 'Photo',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          {params?.row?.profile_picture ? (
            <Avatar src={params.row.profile_picture} alt={`${params.row.first_name} ${params.row.last_name}`} sx={{ width: 36, height: 36 }} />
          ) : (
            <Avatar sx={{ width: 36, height: 36 }}><PersonIcon fontSize="small" /></Avatar>
          )}
        </Box>
      )
    },
    { field: 'first_name', headerName: 'Prénom', width: 160 },
    { field: 'last_name', headerName: 'Nom', width: 160 },
    { field: 'cin', headerName: 'CIN', width: 160 },
    { field: 'profession_name', headerName: 'Profession', flex: 1, minWidth: 160,
      renderCell: (params) => (params?.row?.profession_name || params?.row?.profession?.name || '—')
    },
    { field: 'site_name', headerName: 'Site', flex: 1, minWidth: 160,
      renderCell: (params) => (params?.row?.site_name || params?.row?.site?.name || '—')
    },
    { field: 'hire_date', headerName: "Embauché le", width: 140,
      renderCell: (params) => {
        const v = params?.row?.hire_date;
        return v ? new Date(v).toLocaleDateString() : '';
      }
    },
    { field: 'trial_period_end', headerName: "Fin d'essai", width: 140,
      valueGetter: (params) => params?.row?.trial_period_end || '',
      valueFormatter: ({ value }) => (value ? new Date(value).toLocaleDateString() : '')
    },
    { field: 'leave_balance', headerName: 'Solde congés', width: 130 },
    { field: 'createdAt', headerName: 'Créé le', width: 140,
      renderCell: (params) => (params?.row?.createdAt ? new Date(params.row.createdAt).toLocaleDateString() : '')
    },
    { field: 'archived_at', headerName: 'Archivé le', width: 160,
      renderCell: (params) => (params?.row?.archived_at ? new Date(params.row.archived_at).toLocaleString() : '')
    },
    {
      field: 'view', headerName: 'Détails', width: 120, sortable: false, filterable: false,
      renderCell: (params) => (
        <Button size="small" onClick={() => openDetails(params.row)}>Voir</Button>
      )
    },
    ...(isAdmin ? [{
      field: 'actions', headerName: 'Actions', width: 180, renderCell: (params) => (
        <Button size="small" variant="contained" onClick={() => handleAskUnarchive(params.row.id)}>Désarchiver</Button>
      )
    }] : [])
  ];

  const filteredRows = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.first_name || '').toLowerCase().includes(q) ||
      (r.last_name || '').toLowerCase().includes(q) ||
      (r.cin || '').toLowerCase().includes(q) ||
      (r.profession_name || r.profession?.name || '').toLowerCase().includes(q) ||
      (r.site_name || r.site?.name || '').toLowerCase().includes(q)
    );
  }, [rows, debouncedQuery]);

  return (
    <div>
      <h2>Employés inactifs</h2>
      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Vous n'êtes pas administrateur. Le désarchivage est réservé aux administrateurs.
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} mb={1}>
        <TextField size="small" label="Rechercher" placeholder="Nom, Prénom" value={query} onChange={(e) => setQuery(e.target.value)} sx={{ maxWidth: 320 }} />
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={load} disabled={loading} variant="outlined">Rafraîchir</Button>
      </Stack>
      {(!loading && filteredRows.length === 0) ? (
        <Box p={4} textAlign="center">
          <Typography color="textSecondary">Aucun employé inactif</Typography>
        </Box>
      ) : (
        <DataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(r) => r.id}
          pageSize={10}
          autoHeight
          disableRowSelectionOnClick
          density="compact"
          loading={loading}
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: false } }}
          initialState={{
            sorting: { sortModel: [{ field: 'archived_at', sort: 'desc' }] },
            pagination: { paginationModel: { pageSize: 10, page: 0 } },
            columns: { columnVisibilityModel: { createdAt: false, trial_period_end: false } }
          }}
        />
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirmer le désarchivage</DialogTitle>
        <DialogContent>Voulez-vous vraiment désarchiver cet employé ?</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleConfirmUnarchive}>Confirmer</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>

      {/* Details Drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={closeDetails} PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar src={selectedEmployee?.profile_picture}>
              {!selectedEmployee?.profile_picture && <PersonIcon />}
            </Avatar>
            <Box>
              <Typography variant="h6">{selectedEmployee ? `${selectedEmployee.first_name} ${selectedEmployee.last_name}` : ''}</Typography>
              <Typography variant="body2" color="text.secondary">CIN: {selectedEmployee?.cin || '—'}</Typography>
              <Typography variant="body2" color="text.secondary">{selectedEmployee?.profession?.name || ''} • {selectedEmployee?.site?.name || ''}</Typography>
            </Box>
          </Stack>
        </Box>
        <Divider />
        <Tabs value={activeTab} onChange={(_, v) => { setActiveTab(v); if (selectedEmployee) loadTabData(v, selectedEmployee.id); }} variant="scrollable">
          <Tab label="Champs perso" value="custom" />
          <Tab label="Bulletins" value="payslips" />
          <Tab label="Congés" value="leaves" />
          <Tab label="Missions" value="missions" />
          <Tab label="Sorties" value="exit" />
          <Tab label="Décharges" value="decharge" />
        </Tabs>
        <Box sx={{ p: 2 }}>
          {loadingTab && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!loadingTab && activeTab === 'custom' && (
            <Box>
              {customFields ? (
                <List dense>
                  {(customFields.metadata?.order || []).map((key) => (
                    <ListItem key={key}>
                      <ListItemText
                        primary={customFields.metadata?.fields?.[key]?.label || key}
                        secondary={String(customFields.values?.[key] ?? '—')}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">Aucun champ personnalisé.</Typography>
              )}
            </Box>
          )}
          {!loadingTab && activeTab === 'payslips' && (
            <Box>
              {Array.isArray(payslips) && payslips.length > 0 ? (
                <List dense>
                  {payslips.map((p) => (
                    <ListItem key={p.id} divider>
                      <ListItemText primary={`Bulletin #${p.id}`} secondary={p.createdAt ? new Date(p.createdAt).toLocaleDateString() : ''} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">Aucun bulletin.</Typography>
              )}
            </Box>
          )}
          {!loadingTab && activeTab === 'leaves' && (
            <Box>
              {Array.isArray(leaves) && leaves.length > 0 ? (
                <List dense>
                  {leaves.map((l) => (
                    <ListItem key={l.id} divider>
                      <ListItemText primary={`${new Date(l.start_date).toLocaleDateString()} → ${new Date(l.end_date).toLocaleDateString()}`} secondary={l.status || ''} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">Aucun congé.</Typography>
              )}
            </Box>
          )}
          {!loadingTab && activeTab === 'missions' && (
            <Box>
              {Array.isArray(missions) && missions.length > 0 ? (
                <List dense>
                  {missions.map((m) => (
                    <ListItem key={m.id} divider>
                      <ListItemText primary={`Ordre ${m.order_number || m.id}`} secondary={`${new Date(m.validity_from).toLocaleDateString()} → ${new Date(m.validity_to).toLocaleDateString()}`} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">Aucune mission.</Typography>
              )}
            </Box>
          )}
          {!loadingTab && activeTab === 'exit' && (
            <Box>
              {Array.isArray(exitAuths) && exitAuths.length > 0 ? (
                <List dense>
                  {exitAuths.map((ea) => (
                    <ListItem key={ea.id} divider>
                      <ListItemText primary={`Autorisation #${ea.id}`} secondary={ea.createdAt ? new Date(ea.createdAt).toLocaleDateString() : ''} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">Aucune autorisation de sortie.</Typography>
              )}
            </Box>
          )}
          {!loadingTab && activeTab === 'decharge' && (
            <Box>
              {Array.isArray(decharges) && decharges.length > 0 ? (
                <List dense>
                  {decharges.map((d) => (
                    <ListItem key={d.id} divider>
                      <ListItemText primary={`Décharge #${d.id}`} secondary={d.createdAt ? new Date(d.createdAt).toLocaleDateString() : ''} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">Aucune décharge.</Typography>
              )}
            </Box>
          )}
        </Box>
      </Drawer>
    </div>
  );
};

export default InactifsPage;
